import * as React from 'react';
import { createRootRouteWithContext, Outlet, useNavigate } from '@tanstack/react-router';
import type { RouterAuthContext } from '@/App';
import { GlobalDetailPanelProvider } from '@/components/providers/GlobalDetailPanelProvider';
import { ToastProvider } from '@/components/ui/toast';
import { GlobalDetailPanelRenderer } from '@/components/ui/global-detail-panel-renderer';
import { FullPageSpinner } from '@/components/ui/full-page-spinner';
import { FullPageError } from '@/components/ui/full-page-error';

const TanStackRouterDevtools = import.meta.env.PROD
  ? () => null
  : React.lazy(() =>
      import('@tanstack/react-router-devtools').then((res) => ({
        default: res.TanStackRouterDevtools,
      })),
    );

export const Route = createRootRouteWithContext<{ auth: RouterAuthContext }>()({
  component: RootComponent,
  pendingComponent: RootPendingComponent,
  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
});

function RootComponent() {
  return (
    <GlobalDetailPanelProvider>
      <ToastProvider>
        <Outlet />
        <GlobalDetailPanelRenderer />
        <React.Suspense>
          <TanStackRouterDevtools position="bottom-right" />
        </React.Suspense>
      </ToastProvider>
    </GlobalDetailPanelProvider>
  );
}

function RootPendingComponent() {
  return <FullPageSpinner />;
}

function RootErrorComponent({ error }: { error: Error }) {
  return (
    <FullPageError
      title="Something went wrong"
      description={error.message}
      actionLabel="Reload"
      onAction={() => window.location.reload()}
    />
  );
}

function RootNotFoundComponent() {
  const navigate = useNavigate();
  return (
    <FullPageError
      title="Page Not Found"
      description="The page you are looking for does not exist."
      actionLabel="Back to Sequences"
      onAction={() => navigate({ to: '/sequences' })}
    />
  );
}
